import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, distinctUntilChanged, map } from 'rxjs';
import { difference, isEqual } from 'lodash';

import { extractEmails } from 'src/app/common/utils/common-util';
import { getEmailValidationErrors } from 'src/app/common/utils/form-validators';
import { TOAST_CLASSES } from 'src/app/common/utils/notification-constants';
import { User } from 'src/app/models/user';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { ProfilesService } from 'src/app/services/profiles.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { UserSearchInputDirective } from 'src/app/directives/user-search-input.directive';
import { TooltipDirective } from 'src/app/standalones/directives/pncl-tooltip.directive';
import { UserSearchSelectionService } from 'src/app/services/user-search-selection.service';

export interface EmailSelectionEvent {
  emails: string[];
}

export enum EmailValidatorType {
  EXTERNAL_USER = 'external-user',
}

interface EmailValidatorConfig {
  findInvalidEmails: FindInvalidEmailsFn;
  errorMessage: string;
}

// Function that returns a list of emails that failed the validation for the given emails
// If the list is empty, the emails are all passing the validation
type FindInvalidEmailsFn = (emails: string[]) => Promise<string[]>;

@Component({
  selector: 'app-email-search',
  templateUrl: './email-search.component.html',
  styleUrls: ['./email-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * This component can operate in strict or graceful parsing mode.
 * In strict mode, if the input contains one or more invalid email addresses,
 * the component does not parse any emails from the input. Instead, it retains the original input string as-is.
 * In graceful mode, valid emails are extracted and processed even if some invalid ones are present.
 */
export class EmailSearchComponent {
  private _isInvalid = false;
  @Input() emails: string[] = [];
  @Input() suggestUsers = false;
  @Input() showParsedEmailChips = true;
  @Input() placeholder = '';

  /**
   Additional validators to run on the emails. Note that the format validator is always run by default.
   @example [EmailValidatorType.EXTERNAL_USER]
   */
  @Input() additionalValidators: EmailValidatorType[] = [];

  @Output() emitEmails = new EventEmitter<EmailSelectionEvent>();
  @Output() emitUserselected = new EventEmitter<User>();
  @Output() emitBlur = new EventEmitter<void>();

  /**
   * Controls email validation behavior:
   * - When true (default): Rejects all emails if any are invalid
   * - When false: Accepts valid emails even if some invalid ones are present
   */
  @Input() strictParsing = true;

  @ViewChild('emailInvalidTooltip') emailInvalidTooltip?: TooltipDirective;
  @ViewChild(UserSearchInputDirective) userSearchDirective?: UserSearchInputDirective;

  searchQuery = '';
  isSearchFocused = false;
  isSearchHovered = false;
  validEmailInput = false;

  // Map of validator types to validator functions
  validatorsMap: Record<EmailValidatorType, EmailValidatorConfig> = {
    [EmailValidatorType.EXTERNAL_USER]: {
      findInvalidEmails: this.findExternalUserEmails.bind(this),
      errorMessage: this.translateService.instant(
        "People outside your institution can't be invited to this Space",
      ),
    },
  };

  // Properties to store the results of the email validators

  // The key is the email address and the value is the error message
  emailErrorMap$ = new BehaviorSubject<Map<string, string>>(new Map());
  errorMessages$ = this.emailErrorMap$.pipe(
    map((validatedEmails) => Array.from(new Set(validatedEmails.values()))),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
  );

  constructor(
    private telemetry: TelemetryService,
    private notificationToasterService: NotificationToasterService,
    private translateService: TranslateService,
    private profilesService: ProfilesService,
    public userSearchSelectionService: UserSearchSelectionService,
  ) {}

  onFocusSearch(): void {
    this.isSearchFocused = true;
  }

  onBlurSearch(): void {
    this.isSearchFocused = false;
    this.emitBlur.emit();
  }

  onPaste($event: ClipboardEvent): void {
    const pastedText = $event.clipboardData?.getData('text') || '';
    this.searchQuery += pastedText;
    this.onInputChange();
    this.processTypedEmail(this.searchQuery);
    $event.preventDefault();
  }

  onInputChange(): void {
    this.validEmailInput = !getEmailValidationErrors(this.searchQuery);
    this.isInvalid = false;
  }

  onKeyPress($event: KeyboardEvent): void {
    const pressedKey = $event.key;
    if (pressedKey === 'Enter' || pressedKey === ';' || pressedKey === ',') {
      this.processTypedEmail(this.searchQuery);
      $event.preventDefault();
    }
  }

  emitChanges() {
    this.emitEmails.emit({ emails: this.emails });
  }

  async processTypedEmail(inputEmail: string): Promise<void> {
    inputEmail = inputEmail.toLowerCase();
    if (inputEmail.length && !this.emails.includes(inputEmail)) {
      const result = extractEmails(inputEmail, this.telemetry, this.strictParsing);
      if (result) {
        this.isInvalid = false;
        this.emails = [
          ...new Set([...this.emails, ...result.emails, ...(result.invalidEmails || [])]),
        ];

        // Mark invalid emails from parsing
        this.updateEmailErrorMap((errorMap) => {
          result.invalidEmails?.forEach((email) =>
            errorMap.set(email, this.translateService.instant('Invalid email address')),
          );
        });

        // Run additional validators on resulting valid emails
        await this.runValidators(result.emails);

        this.clearSearchInput();
      } else {
        // Show appropriate error based on parsing mode
        this.isInvalid = this.strictParsing;
        if (!this.strictParsing) {
          this.showGracefulParsingError();
        }
      }
    }
    this.emitChanges();
  }

  onRemoveUser(emailToRemove: string): void {
    this.emails = this.emails.filter((email) => email !== emailToRemove);

    this.updateEmailErrorMap((errorMap) => errorMap.delete(emailToRemove));

    this.emitChanges();
  }

  async addUser(email: string) {
    this.clearSearchInput();
    this.emails.push(email);

    await this.runValidators([email]);

    this.emitChanges();
  }

  async selectUser(user: User): Promise<void> {
    this.clearSearchInput();

    await this.runValidators([user.email]);

    this.emitUserselected.emit(user);
  }

  /** Clears the search input and dropdown results (e.g. after adding/selecting a user). */
  private clearSearchInput(): void {
    // Directive clears the input and dispatches 'input' so its query stream sees '' and emits [];
    // programmatic ngModel change does not fire native input events.
    this.userSearchDirective?.clear();
  }

  private handleTooltip(): void {
    if (this.isInvalid) {
      this.emailInvalidTooltip?.open();
    } else {
      this.emailInvalidTooltip?.close();
    }
  }

  get isInvalid(): boolean {
    return this._isInvalid;
  }

  private set isInvalid(value: boolean) {
    this._isInvalid = value;
    this.handleTooltip();
  }

  get emailErrorMap(): Map<string, string> {
    return this.emailErrorMap$.value;
  }

  showGracefulParsingError(): void {
    this.notificationToasterService.showDefaultErrorNotificationV2(
      this.translateService.instant('Sorry, there was a problem'),
      this.translateService.instant(
        'We weren’t able to extract valid emails from the contents of your clipboard',
      ),
      this.notificationToasterService.generateNotificationCode(),
      undefined,
      undefined,
      5,
      true,
      undefined,
      TOAST_CLASSES.HIGH_Z_INDEX,
    );
  }

  userTrackBy(index: number, user: User) {
    return user._id;
  }

  resetErrorState(): void {
    this.emailErrorMap$.next(new Map());
  }

  private updateEmailErrorMap(updateFn: (errorMap: Map<string, string>) => void): void {
    const updatedErrorMap = new Map(this.emailErrorMap$.value);
    updateFn(updatedErrorMap);
    this.emailErrorMap$.next(updatedErrorMap);
  }

  /**
   * Runs all validators on the given emails and updates the emailErrorMap property
   * @param emails - The emails to validate
   * @returns void
   */
  private async runValidators(emails: string[]): Promise<void> {
    try {
      let emailsToValidate = [...emails];

      if (!this.additionalValidators.length) {
        return;
      }

      for (const validatorType of this.additionalValidators) {
        if (!emailsToValidate.length) {
          break;
        }

        const findInvalidEmails = this.validatorsMap[validatorType].findInvalidEmails;
        const invalidEmails = await findInvalidEmails(emailsToValidate);

        this.updateEmailErrorMap((errorMap) => {
          invalidEmails.forEach((email) =>
            errorMap.set(email, this.validatorsMap[validatorType].errorMessage),
          );
        });

        // Remove failed emails from the list of emails to validate
        emailsToValidate = difference(emailsToValidate, invalidEmails);
      }
    } catch (error) {
      console.error('Unexpected error running email validators', error);

      this.notificationToasterService.showDefaultErrorNotificationV2(
        this.translateService.instant('Sorry, there was a problem'),
        this.translateService.instant(
          'An error occurred while validating emails. Please try again.',
        ),
        this.notificationToasterService.generateNotificationCode(),
        undefined,
        undefined,
        undefined,
        true,
        undefined,
        TOAST_CLASSES.HIGH_Z_INDEX,
      );

      // Remove all emails being validated from the current list of emails
      // This is to avoid emitting these emails when an overall error occurs
      this.emails = difference(this.emails, emails);
    }
  }

  // Filter functions

  /**
   * Filter function that returns emails that are external to the institution when institution settings prevent it
   */
  private async findExternalUserEmails(emails: string[]): Promise<string[]> {
    const response = await this.profilesService.getInstitutionUsers(1, emails.length, { emails });
    const institutionEmails = response?.users.map((user) => user.email) || [];
    const externalEmails = difference(emails, institutionEmails);
    return externalEmails;
  }
}
